import { ReloadOutlined } from '@ant-design/icons';
import { Button, Col, Row, Spin, Table, Tooltip, App } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import i18next from 'i18next';
import Moment from 'moment';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src';
import { getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { AppConfig, container } from 'src/inversify.config';
import { OrganizationBackupItem, OrganizationBackupStore, OrganizationItemStore } from 'src/stores/organizations-store';

const OrganizationBackupTab: FC<{ organizationId: string }> = ({ organizationId }) => {
  const { t } = useTranslation();
  const organizationStore = container.get(OrganizationBackupStore);
  const organizationItemStore = container.get(OrganizationItemStore);

  const [backupData, setBackupData] = useState<OrganizationBackupItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { notification, modal } = App.useApp();
  const config: AppConfiguration = useContext(AppConfig);

  useEffect(() => {
    loadBackupData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBackupData = async () => {
    const data = await organizationStore.getByOrganizationId(organizationId);
    setIsLoading(false);
    setBackupData(data);
  };

  const onReload = async () => {
    setIsLoading(true);
    await loadBackupData();
  };

  const columns: ColumnsType<OrganizationBackupItem> = [
    {
      title: t('CreatedOn'),
      dataIndex: 'createdOn',
      render: (_, record: OrganizationBackupItem) => {
        const formattedDate = Moment.utc(record.createOn).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`);
        return <span>{formattedDate}</span>;
      },
    },
    {
      dataIndex: 'organizationName',
      title: t('OrganizationName'),
      render: (_, record: OrganizationBackupItem) => <span>{record.organizationName}</span>,
    },
    {
      title: '',
      dataIndex: 'backupId',
      render: (_, record: OrganizationBackupItem) => {
        return (
          <>
            <Button
              type="dashed"
              className="solid-button"
              onClick={() => {
                onRestoreBackup(record.backupId, record.organizationId);
              }}
              style={{ marginRight: 10, marginBottom: 10 }}
            >
              {t('Restore')}
            </Button>
            <Button
              type="dashed"
              className="solid-button"
              onClick={() => {
                downloadBackup(record.backupId, record.organizationId);
              }}
            >
              {t('Download')}
            </Button>
          </>
        );
      },
    },
  ];

  const downloadBackup = async (backupId: string, organizationId: string) => {
    const response = await organizationStore.generateDownloadUrl(backupId, organizationId);

    if (response.isSuccess) {
      const link = document.createElement('a');
      link.download = `${backupId}.eiox`;
      link.href = response.url;
      link.click();
    } else {
      notification.error({ placement: 'bottomRight', message: t('AnErrorOcurred') });
    }
  };

  const onRestoreBackup = async (backupId: string, organizationId: string) => {
    const organizationIsLocked = await organizationItemStore.checkLockedStatus(organizationId);

    if (organizationIsLocked) {
      notification.error({
        placement: 'bottomRight',
        duration: 20,
        style: {
          width: 600,
        },
        message: t('OrganizationIsLocked'),
      });
      return;
    }

    modal.confirm({
      title: t('RestoreBackupConfirmMessage'),
      cancelButtonProps: { type: 'primary' },
      onOk: async () => {
        setIsLoading(true);
        const response = await organizationStore.restoreOrganization(backupId, organizationId);
        console.log(response);
        if (response?.isSuccess) {
          notification.success({
            message: t('RestoreBackupSuccess'),
            placement: 'bottomRight',
          });
        } else {
          notification.error({
            message: config.environment === ENV.DEV ? response?.error : t('ErrorRestoringOrganization'),
            placement: 'bottomRight',
          });
        }
        loadBackupData();
      },
    });
  };

  return (
    <Spin spinning={isLoading}>
      <div>
        <Row style={{ padding: '0 40px' }}>
          <Col lg={12} xs={24}>
            <Row gutter={[8, 0]}>
              <Col>
                <h3 style={{ marginTop: 12 }}>{t('Backups')}</h3>
              </Col>
            </Row>
          </Col>
          <Col lg={12} xs={24}>
            <Row justify="end">
              <Col>
                <Tooltip placement="top" title={t('Reload')}>
                  <Button
                    className="ant-btn-secondary"
                    icon={<ReloadOutlined style={{ color: '#AB162B' }} />}
                    onClick={onReload}
                    style={{ marginBottom: 16, marginRight: 10, width: 56 }}
                  ></Button>
                </Tooltip>
              </Col>
            </Row>
          </Col>
        </Row>
        <div>
          <Table rowKey="id" columns={columns} dataSource={backupData} />
        </div>
      </div>
    </Spin>
  );
};

export default OrganizationBackupTab;
